import {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../redux/hooks'
import Loader from 'app/custom/loader/Loader'
import ReactApexChart from 'react-apexcharts'
import Chart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import {PaymentGraph, InvoiceSummary} from '../../../redux/Slice/financeSlice'
import {Link, useNavigate} from 'react-router-dom'
import {encryptId} from '_metronic/helpers/Encrypt'
import {PlanningAcademicYears} from '../../../redux/Slice/generalSlice'
import CustomSelect from 'app/custom/CustomSelect'

const FinanceDashboard = () => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [academicYears, setAcademicYears] = useState<any>([])
  const [selectedAy, setSelectedAy] = useState<any>('')
  const [loaderPayment, setLoaderPayment] = useState(false)
  const [months, setMonths] = useState<string[]>([])
  const [paid, setPaid] = useState<number[]>([])
  const [due, setDue] = useState<number[]>([])
  const [late, setLate] = useState<number[]>([])
  const [monthYears, setMonthYears] = useState<number[]>([])

  const [loaderSummary, setLoaderSummary] = useState(true)
  const [statistics, setStatistics] = useState<any>({})

  const fetchPaymentData = (selectedYear: number) => {
    setLoaderPayment(true)
    dispatch(InvoiceSummary(selectedYear)).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setStatistics(res.payload || {})
        setLoaderSummary(false)
      }
    })
    const fd = new FormData()
    fd.append('year', String(selectedYear))
    dispatch(PaymentGraph(fd)).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        const data = res.payload || []
        setMonths(data.map((i: any) => `${i.month}-${i.year}`))
        setPaid(data.map((i: any) => i.paid))
        setDue(data.map((i: any) => i.due))
        setLate(data.map((i: any) => i.late_payments))
        setMonthYears(data.map((i: any) => Number(i.year)))
      }
      setLoaderPayment(false)
    })
  }

  const paymentSeries = [
    {name: t('LABEL.PAID'), data: paid},
    {name: t('LABEL.REMAIN'), data: due},
    {name: t('LABEL.LATE_PAYMENT'), data: late},
  ]

  const academicMonthNumbers = [8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6, 7]

  const paymentOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 400,
      toolbar: {show: false},
      events: {
        dataPointSelection: (event, chartContext, config) => {
          let type: 'paid' | 'due' | 'late' = 'paid'
          if (config.seriesIndex === 1) type = 'due'
          if (config.seriesIndex === 2) type = 'late'

          const index = config.dataPointIndex
          navigate('/admin/std-payments', {
            state: {
              year: monthYears[index],
              month: academicMonthNumbers[index],
              type,
            },
          })
        },
      },
    },

    plotOptions: {
      bar: {
        columnWidth: '60%',
        borderRadius: 6,
      },
    },

    xaxis: {
      categories: months,
      labels: {style: {fontWeight: 'bold'}},
    },

    legend: {position: 'bottom'},

    colors: ['#50C878', '#F5A623', '#D0021B'],

    dataLabels: {enabled: false},
  }

  const paidTotal = Number(statistics?.total_paid ?? 0)
  const unpaidTotal = Number(statistics?.total_unpaid ?? 0)
  const latePayments = Number(statistics?.late_payments ?? 0)

  const donutSeries = paidTotal + unpaidTotal > 0 ? [paidTotal, unpaidTotal, latePayments] : [1, 1]

  const donutOptions: ApexCharts.ApexOptions = {
    chart: {type: 'donut'},

    labels: [t('LABEL.TOTAL_PAID'), t('LABEL.TOTAL_DUE'), t('LABEL.LATE_PAYMENT')],

    colors: ['#2DB36C', '#7239ea', '#f1416c'],
    legend: {show: false},
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${Math.round(val)}%`,
    },

    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: false,
          },
        },
      },
    },
    tooltip: {
      y: {
        formatter: (value: number) => {
          return value.toLocaleString()
        },
      },
    },
  }

  const selectedAyRef = useRef<number | null>(null)

  const handleAcademicYearChange = (item: any) => {
    setSelectedAy(item.id)
    if (item.id) {
      selectedAyRef.current = item.id
      fetchPaymentData(item.id)
    }
  }

  useEffect(() => {
    dispatch(PlanningAcademicYears()).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        const years = res.payload
        const sorted = [...years].sort((a: any, b: any) => a.name.localeCompare(b.name))
        setAcademicYears(sorted)
        const lastAcademicYear = sorted.at(-1)
        if (lastAcademicYear) {
          setSelectedAy(lastAcademicYear.id)
          selectedAyRef.current = lastAcademicYear.id
          fetchPaymentData(lastAcademicYear.id)
        } else {
          setLoaderSummary(false)
          setLoaderPayment(false)
        }
      }
    })
  }, [])

  if (loaderSummary) return <Loader />

  return (
    <div className='col-12 mt-3 p-5'>
      <div className='card shadow-sm border-0'>
        <div className='card-body pt-5 pb-0'>
          <label className='fw-bold mb-2 mx-3'>{t('LABEL.ACADEMIC_YEAR')}</label>
          <CustomSelect
            options={academicYears}
            value={selectedAy}
            onChange={handleAcademicYearChange}
          />
        </div>
        {loaderPayment && <Loader />}
        {!loaderPayment && (
          <>
            <div className='card-body'>
              <h4 className='fw-bold mb-4'>{t('DASHBOARD.AT_A_GLANCE')}</h4>
              <div className='d-flex align-items-center'>
                <Chart options={donutOptions} series={donutSeries} type='donut' width={250} />

                <div className='ms-4 w-100'>
                  <Link
                    to={`/finance/inv-sum-details/${encryptId('')}/${encryptId(
                      'All'
                    )}/${selectedAy}`}
                    className='text-decoration-none'
                  >
                    <div className='d-flex justify-content-between bg-light rounded px-3 py-2 mb-2'>
                      <span className='fw-bold text-dark fs-4'>{t('LABEL.TOTAL_AMOUNT')}</span>
                      <span className='text-dark fw-bold fs-4'>
                        {statistics?.currency ?? ''} {statistics?.total_invoices ?? 0}
                      </span>
                    </div>
                  </Link>

                  <Link
                    to={`/finance/inv-sum-details/${encryptId('LABEL.PAID')}/${encryptId(
                      'Paid'
                    )}/${selectedAy}`}
                    className='text-decoration-none'
                  >
                    <div className='d-flex justify-content-between bg-light-success rounded px-3 py-2 mb-2'>
                      <span className='fw-bold text-success fs-4'>{t('LABEL.TOTAL_PAID')}</span>
                      <span className='fw-bold text-success fs-4'>{statistics?.currency ?? ''} {paidTotal}</span>
                    </div>
                  </Link>

                  <Link
                    to={`/finance/inv-sum-details/${encryptId('LABEL.REMAIN')}/${encryptId(
                      'Due'
                    )}/${selectedAy}`}
                    className='text-decoration-none'
                  >
                    <div className='d-flex justify-content-between bg-light-info rounded px-3 py-2 mb-2'>
                      <span className='fw-bold text-info fs-4'>{t('LABEL.TOTAL_DUE')}</span>
                      <span className='fw-bold text-info fs-4'>{statistics?.currency ?? ''} {unpaidTotal}</span>
                    </div>
                  </Link>
                  <Link
                    to={`/finance/inv-sum-details/${encryptId('LABEL.LATE_PAYMENT')}/${encryptId(
                      'late_payment'
                    )}/${selectedAy}`}
                    className='text-decoration-none'
                  >
                    <div className='d-flex justify-content-between bg-light-danger rounded px-3 py-2'>
                      <span className='fw-bold text-danger fs-4'>{t('LABEL.LATE_PAYMENT')}</span>
                      <span className='fw-bold text-danger fs-4'>{statistics?.currency ?? ''} {latePayments}</span>
                    </div>
                  </Link>
                </div>
              </div>
            </div>

            <div className='card-body'>
              {loaderPayment && <Loader />}
              <hr />
              <ReactApexChart
                options={paymentOptions}
                series={paymentSeries}
                type='bar'
                height={350}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default FinanceDashboard
